// react
import React from 'react';

// third-party
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

/**
 * Clean empty state (same look as the empty cart).
 * variant "cart" uses the cart illustration; others show a card with a badge icon.
 */
function EmptyState(props) {
    const { icon, title, text, actionLabel, actionTo, actionIcon, onAction, compact, className } = props;
    const isCart = icon === 'cart';
    const is404 = icon === '404' || icon === 'not-found';

    return (
        <div className={`hokky-empty-cart-card hokky-empty-state ${compact ? 'hokky-empty-state--compact' : ''} ${className}`}>
            <div className="hokky-empty-illustration-wrapper">
                <div className="empty-ambient-glow" />
                {isCart ? (
                    <svg className="empty-cart-svg" width="150" height="130" viewBox="0 0 160 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <linearGradient id="esCartGrad" x1="20" y1="30" x2="130" y2="110" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#0064d2" />
                                <stop offset="1" stopColor="#0050be" />
                            </linearGradient>
                            <linearGradient id="esBasketGrad" x1="30" y1="45" x2="115" y2="95" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#ffffff" />
                                <stop offset="1" stopColor="#f8fafc" />
                            </linearGradient>
                            <linearGradient id="esSparkle" x1="0" y1="0" x2="20" y2="20" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#f59e0b" />
                                <stop offset="1" stopColor="#fbbf24" />
                            </linearGradient>
                        </defs>
                        <ellipse cx="80" cy="116" rx="52" ry="9" fill="#e2e8f0" opacity="0.8" />
                        <circle cx="32" cy="42" r="4" fill="#fbbf24" opacity="0.9" />
                        <circle cx="132" cy="70" r="3.5" fill="#60a5fa" opacity="0.8" />
                        <circle cx="26" cy="80" r="2.5" fill="#93c5fd" opacity="0.7" />
                        <path d="M126 24L128 17L135 19L130 24L132 31L126 26L120 31L122 24L117 19L124 17Z" fill="url(#esSparkle)" />
                        <rect x="36" y="46" width="76" height="44" rx="14" fill="url(#esBasketGrad)" stroke="#cbd5e1" strokeWidth="2.5" />
                        <path d="M48 47V89M62 47V89M76 47V89M90 47V89" stroke="#e2e8f0" strokeWidth="1.8" strokeLinecap="round" />
                        <path d="M37 60H111M37 74H111" stroke="#e2e8f0" strokeWidth="1.8" strokeLinecap="round" />
                        <path d="M112 52L123 38C124.5 36.5 126.5 35.5 128.5 35.5H135" stroke="url(#esCartGrad)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="53" cy="102" r="8.5" fill="#0f172a" />
                        <circle cx="53" cy="102" r="3.5" fill="#ffffff" />
                        <circle cx="95" cy="102" r="8.5" fill="#0f172a" />
                        <circle cx="95" cy="102" r="3.5" fill="#ffffff" />
                        <path d="M53 90V94M95 90V94" stroke="#64748b" strokeWidth="3" strokeLinecap="round" />
                        <rect x="62" y="34" width="22" height="22" rx="6" fill="#0064d2" transform="rotate(-6 62 34)" />
                        <path d="M70 43C70 41.5 71.5 40 73 40C74.5 40 76 41.5 76 43C76 45.5 73 48 73 48C73 48 70 45.5 70 43Z" fill="#ffffff" transform="rotate(-6 62 34)" />
                    </svg>
                ) : is404 ? (
                    <svg className="empty-cart-svg empty-404-svg" width="160" height="136" viewBox="0 0 160 136" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <defs>
                            <linearGradient id="es404Blue" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#0064d2" />
                                <stop offset="100%" stopColor="#004eb8" />
                            </linearGradient>
                            <linearGradient id="es404Card" x1="30" y1="20" x2="130" y2="115" gradientUnits="userSpaceOnUse">
                                <stop offset="0%" stopColor="#ffffff" />
                                <stop offset="100%" stopColor="#f8fafc" />
                            </linearGradient>
                            <linearGradient id="es404Glass" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor="#eff6ff" stopOpacity="0.9" />
                                <stop offset="100%" stopColor="#bfdbfe" stopOpacity="0.45" />
                            </linearGradient>
                            <linearGradient id="es404Sparkle" x1="0" y1="0" x2="20" y2="20" gradientUnits="userSpaceOnUse">
                                <stop offset="0%" stopColor="#f59e0b" />
                                <stop offset="100%" stopColor="#fbbf24" />
                            </linearGradient>
                            <linearGradient id="es404SoftPill" x1="0" y1="0" x2="1" y2="0">
                                <stop offset="0%" stopColor="#dbeafe" />
                                <stop offset="100%" stopColor="#eff6ff" />
                            </linearGradient>
                            <filter id="es404Glow" x="-20%" y="-20%" width="140%" height="140%">
                                <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="#0064d2" floodOpacity="0.2" />
                            </filter>
                            <filter id="esGlassShadow" x="-30%" y="-30%" width="160%" height="160%">
                                <feDropShadow dx="0" dy="5" stdDeviation="5" floodColor="#0f172a" floodOpacity="0.16" />
                            </filter>
                        </defs>

                        {/* Ground Shadow */}
                        <ellipse cx="80" cy="122" rx="54" ry="8" fill="#e2e8f0" opacity="0.8" />
                        <ellipse cx="80" cy="122" rx="34" ry="5" fill="#cbd5e1" opacity="0.55" />

                        {/* Sparkles & Floating Dots */}
                        <path d="M136 20L138 13L145 15L140 20L142 27L136 22L130 27L132 20L127 15L134 13Z" fill="url(#es404Sparkle)" />
                        <circle cx="26" cy="38" r="4.5" fill="#fbbf24" opacity="0.9" />
                        <circle cx="20" cy="84" r="3" fill="#93c5fd" opacity="0.75" />
                        <circle cx="142" cy="70" r="3.5" fill="#60a5fa" opacity="0.8" />
                        <circle cx="134" cy="100" r="2.5" fill="#fbbf24" opacity="0.85" />

                        {/* Browser Window / Web Page Card */}
                        <rect x="30" y="22" width="100" height="92" rx="18" fill="url(#es404Card)" stroke="#cbd5e1" strokeWidth="2.2" />

                        {/* Browser Header Bar with window dots */}
                        <path d="M30 40C30 30 38 22 48 22H112C122 22 130 30 130 40V44H30V40Z" fill="#f1f5f9" />
                        <line x1="30" y1="44" x2="130" y2="44" stroke="#e2e8f0" strokeWidth="1.5" />
                        <circle cx="42" cy="33" r="3" fill="#ef4444" opacity="0.85" />
                        <circle cx="51" cy="33" r="3" fill="#f59e0b" opacity="0.85" />
                        <circle cx="60" cy="33" r="3" fill="#10b981" opacity="0.85" />

                        {/* Mini URL Bar in Browser Header */}
                        <rect x="71" y="28" width="48" height="10" rx="5" fill="#ffffff" stroke="#e2e8f0" strokeWidth="1" />
                        <circle cx="76" cy="33" r="1.5" fill="#94a3b8" />
                        <line x1="81" y1="33" x2="108" y2="33" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" />

                        {/* Big Bold 404 Typography */}
                        <text
                            x="80"
                            y="72"
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill="url(#es404Blue)"
                            fontSize="32"
                            fontWeight="900"
                            fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
                            letterSpacing="2"
                            filter="url(#es404Glow)"
                        >
                            404
                        </text>

                        {/* Pill badge: 404 NOT FOUND */}
                        <rect x="46" y="86" width="68" height="15" rx="7.5" fill="url(#es404SoftPill)" stroke="#bfdbfe" strokeWidth="1" />
                        <text
                            x="80"
                            y="94.5"
                            textAnchor="middle"
                            dominantBaseline="central"
                            fill="#0064d2"
                            fontSize="7.5"
                            fontWeight="700"
                            letterSpacing="0.8"
                            fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
                        >
                            404 NOT FOUND
                        </text>

                        {/* Floating Magnifying Glass searching the 404 page */}
                        <g filter="url(#esGlassShadow)">
                            {/* Handle */}
                            <path d="M116 95L132 111" stroke="#0f172a" strokeWidth="6" strokeLinecap="round" />
                            <path d="M116 95L123 102" stroke="#0064d2" strokeWidth="6" strokeLinecap="round" />
                            {/* Lens Rim */}
                            <circle cx="105" cy="84" r="16" fill="url(#es404Glass)" stroke="url(#es404Blue)" strokeWidth="3.5" />
                            {/* Glass Glare */}
                            <path d="M97 77C99 74 103 72 108 72" stroke="#ffffff" strokeWidth="2.5" strokeLinecap="round" opacity="0.9" />
                            <circle cx="114" cy="78" r="1.5" fill="#ffffff" opacity="0.9" />
                            {/* Question mark inside lens */}
                            <path d="M103 81C103 79.5 104 78.5 105.5 78.5C107 78.5 108 79.5 108 81C108 82.5 105.5 83 105.5 85" stroke="#0064d2" strokeWidth="1.8" strokeLinecap="round" fill="none" />
                            <circle cx="105.5" cy="87.5" r="1" fill="#0064d2" />
                        </g>
                    </svg>
                ) : (
                    <div className="empty-generic-art">
                        <svg className="empty-cart-svg" width="150" height="130" viewBox="0 0 160 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <defs>
                                <linearGradient id="esCardGrad" x1="40" y1="30" x2="120" y2="110" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#ffffff" />
                                    <stop offset="1" stopColor="#f1f5f9" />
                                </linearGradient>
                                <linearGradient id="esSparkle2" x1="0" y1="0" x2="20" y2="20" gradientUnits="userSpaceOnUse">
                                    <stop stopColor="#f59e0b" />
                                    <stop offset="1" stopColor="#fbbf24" />
                                </linearGradient>
                            </defs>
                            <ellipse cx="80" cy="120" rx="48" ry="8" fill="#e2e8f0" opacity="0.8" />
                            <circle cx="30" cy="44" r="4" fill="#fbbf24" opacity="0.9" />
                            <circle cx="134" cy="76" r="3.5" fill="#60a5fa" opacity="0.8" />
                            <circle cx="26" cy="84" r="2.5" fill="#93c5fd" opacity="0.7" />
                            <path d="M128 22L130 15L137 17L132 22L134 29L128 24L122 29L124 22L119 17L126 15Z" fill="url(#esSparkle2)" />
                            <rect x="38" y="32" width="84" height="80" rx="16" fill="url(#esCardGrad)" stroke="#cbd5e1" strokeWidth="2.5" />
                            <path d="M54 94H106" stroke="#e2e8f0" strokeWidth="4" strokeLinecap="round" />
                        </svg>
                        <span className="empty-generic-badge">
                            <i className={`fas fa-${icon}`} />
                        </span>
                    </div>
                )}
            </div>
            <h3>{title}</h3>
            {text && <p>{text}</p>}
            {actionLabel && actionTo && (
                <Link to={actionTo} className="btn-empty-shop">
                    {actionIcon && <i className={`fas fa-${actionIcon}`} />} {actionLabel}
                </Link>
            )}
            {actionLabel && !actionTo && onAction && (
                <button type="button" className="btn-empty-shop" onClick={onAction}>
                    {actionIcon && <i className={`fas fa-${actionIcon}`} />} {actionLabel}
                </button>
            )}
        </div>
    );
}

EmptyState.propTypes = {
    /** 'cart' or a FontAwesome icon name (without "fa-") */
    icon: PropTypes.string,
    title: PropTypes.string.isRequired,
    text: PropTypes.string,
    actionLabel: PropTypes.string,
    actionTo: PropTypes.string,
    actionIcon: PropTypes.string,
    onAction: PropTypes.func,
    compact: PropTypes.bool,
    className: PropTypes.string,
};

EmptyState.defaultProps = {
    icon: 'box-open',
    compact: false,
    className: '',
};

export default EmptyState;
